import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Select } from '@ngxs/store';
import { combineLatest, Observable } from 'rxjs';
import { skipWhile } from 'rxjs/operators';
import { BaseComponent } from 'src/shared/abstracts/base.abstract';
import { OperatorEnum } from '../enums/operator.enum';

import { IFeaturePermissions } from '../interfaces/feature-permissions.interface';
import { IUser } from '../interfaces/users/user.interface';
import { PermissionsService } from '../services/internal/permissions.service';
import { AuthState } from '../store/state/auth.state';
import { HomeworkSchoolState } from '../store/state/homework-school.state';
import { MealsState } from '../store/state/meals.state';
import { NurseryState } from '../store/state/nursery.state';

@Directive({
    selector: '[appCheckPermissions]',
})
export class CheckPermissionsDirective extends BaseComponent implements OnInit {
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(AuthState.user) public user$: Observable<IUser>;
    @Select(MealsState.getIsMealsSettingsLoaded) public mealsSettingsLoaded$: Observable<boolean>;
    @Select(NurseryState.getIsNurserySettingsLoaded) public nurserySettingsLoaded$: Observable<boolean>;
    @Select(HomeworkSchoolState.getIsHomeworkSchoolSettingsLoaded) public homeworkSettingsLoaded$: Observable<boolean>;
    @Select(AuthState.isSchoolSettingsLoaded) public isSchoolSettingsLoaded$: Observable<boolean>;

    @Input() public appCheckPermissions: IFeaturePermissions;

    constructor(
        private permissionService: PermissionsService,
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.uns = combineLatest([
            this.user$,
            this.currentUser$,
            this.mealsSettingsLoaded$,
            this.nurserySettingsLoaded$,
            this.homeworkSettingsLoaded$,
            this.isSchoolSettingsLoaded$,
        ])
            .pipe(
                skipWhile(
                    ([
                         connectedUser,
                         currentUser,
                         mealsSettingsLoaded,
                         nurserySettingsLoaded,
                         homeworkSettingsLoaded,
                         schoolSettingsLoaded,
                     ]: [IUser, IUser, boolean, boolean, boolean, boolean]) =>
                        currentUser === null ||
                        connectedUser === null ||
                        mealsSettingsLoaded === false ||
                        nurserySettingsLoaded === false ||
                        homeworkSettingsLoaded === false ||
                        schoolSettingsLoaded === false
                )
            )
            .subscribe(([user, currentUser]: [IUser, IUser, boolean, boolean, boolean, boolean]) => {
                if (
                    !!user &&
                    currentUser &&
                    this.permissionService.checkPermission(user.accessLevel, currentUser.accessLevel, this.appCheckPermissions)
                ) {
                    this.viewContainer.clear();
                    this.viewContainer.createEmbeddedView(this.templateRef);
                } else {
                    this.viewContainer.clear();
                }
            });
    }
}

@Directive({
    selector: '[appCheckPermissionsArray]',
})
export class CheckPermissionsArrayDirective extends BaseComponent implements OnInit {
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(AuthState.user) public user$: Observable<IUser>;
    @Select(MealsState.getIsMealsSettingsLoaded) public mealsSettingsLoaded$: Observable<boolean>;
    @Select(NurseryState.getIsNurserySettingsLoaded) public nurserySettingsLoaded$: Observable<boolean>;
    @Select(HomeworkSchoolState.getIsHomeworkSchoolSettingsLoaded) public homeworkSettingsLoaded$: Observable<boolean>;
    @Select(AuthState.isSchoolSettingsLoaded) public isSchoolSettingsLoaded$: Observable<boolean>;

    @Input() public appCheckPermissionsArray: { permissions: IFeaturePermissions[]; operator: string };

    constructor(
        private permissionService: PermissionsService,
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef
    ) {
        super();
    }

    ngOnInit(): void {
        this.uns = combineLatest([
            this.user$,
            this.currentUser$,
            this.mealsSettingsLoaded$,
            this.nurserySettingsLoaded$,
            this.homeworkSettingsLoaded$,
            this.isSchoolSettingsLoaded$,
        ])
            .pipe(
                skipWhile(
                    ([
                         connectedUser,
                         currentUser,
                         mealsSettingsLoaded,
                         nurserySettingsLoaded,
                         homeworkSettingsLoaded,
                         schoolSettingsLoaded,
                     ]: [IUser, IUser, boolean, boolean, boolean, boolean]) =>
                        currentUser === null ||
                        connectedUser === null ||
                        mealsSettingsLoaded === false ||
                        nurserySettingsLoaded === false ||
                        homeworkSettingsLoaded === false ||
                        schoolSettingsLoaded === false
                )
            )
            .subscribe(([user, currentUser]: [IUser, IUser, boolean, boolean, boolean, boolean]) => {
                if (!!user && currentUser) {
                    const permissionsArray: boolean[] = this.appCheckPermissionsArray.permissions.map((p) =>
                        this.permissionService.checkPermission(user.accessLevel, currentUser.accessLevel, p)
                    );
                    if (this.appCheckPermissionsArray.operator === OperatorEnum.AND) {
                        if (permissionsArray.every((p) => p === true)) {
                            this.showContent();
                        }
                    } else if (this.appCheckPermissionsArray.operator === OperatorEnum.OR) {
                        if (permissionsArray.some((p) => p === true)) {
                            this.showContent();
                        }
                    }
                } else {
                    this.viewContainer.clear();
                }
            });
    }

    private showContent(): void {
        this.viewContainer.clear();
        this.viewContainer.createEmbeddedView(this.templateRef);
    }
}
